import React, { useCallback } from 'react';
import ReactFlow, { useReactFlow, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import { mockTableData } from './mockTableData';
import styles from './EntityRelationshipDiagram.module.scss';

// Separate component for zoom controls that uses useReactFlow
const ZoomControls = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const handleZoomIn = useCallback(() => {
    zoomIn();
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut();
  }, [zoomOut]);

  const handleFitView = useCallback(() => {
    fitView();
  }, [fitView]);

  return (
    <div className={styles.zoomControls}>
      <button
        onClick={handleZoomIn}
        className={styles.zoomButton}
        title="Zoom In"
      >
        +
      </button>
      <button
        onClick={handleZoomOut}
        className={styles.zoomButton}
        title="Zoom Out"
      >
        −
      </button>
      <button
        onClick={handleFitView}
        className={styles.fitViewButton}
        title="Fit View"
      >
        ⊞
      </button>
    </div>
  );
};

const EntityRelationshipDiagram = ({ tableColumnData }) => {

  // Generate tables from tableColumnData or use mock data as fallback
  const generateTables = () => {

    // const dataToUse = mockTableData

    const dataToUse = tableColumnData && Array.isArray(tableColumnData) && tableColumnData.length > 0 
      ? tableColumnData 
      : [];


    return dataToUse.map((table, index) => {
      const tableName = table.table_name || `Table_${index + 1}`;
      const columns = table.columns || [];

      // Calculate position based on index
      const row = Math.floor(index / 3);
      const col = index % 3;
      const x = 50 + (col * 350);
      const y = 50 + (row * 300);

      return {
        id: (index + 1).toString(),
        name: tableName,
        properties: columns.map(col => col.column_name || col.name || col),
        position: { x, y },
      };
    });
  };

  // Generate edges based on foreign key relationships
  const generateEdges = (tables) => {
    const edges = [];
    let edgeId = 1;

    tables.forEach((sourceTable, sourceIndex) => {
      tables.forEach((targetTable, targetIndex) => {
        if (sourceIndex === targetIndex) return;

        const targetTableName = targetTable.name.toLowerCase();

        // Check for foreign key relationships
        const hasForeignKey = sourceTable.properties.some(prop =>
          (prop.toLowerCase().includes(targetTableName.replace('s', '')) ||
            prop.toLowerCase().includes('id')) && targetTable.properties.some(targetProp =>
              targetProp.toLowerCase().includes('id')
            )
        );

        if (hasForeignKey) {
          edges.push({
            id: `e${edgeId}`,
            source: sourceTable.id,
            target: targetTable.id,
            label: `${sourceTable.name} → ${targetTable.name}`,
            animated: true,
          });
          edgeId++;
        }
      });
    });

    return edges;
  };

  const tables = generateTables();
  const nodes = tables.map(table => ({
    id: table.id,
    data: {
      label: (
        <div className={styles.tableNode}>
          <div className={styles.tableName}>{table.name}</div>
          <ul className={styles.propertyList}>
            {((mockTableData.find(t => t.table_name === table.name) || {}).columns || table.properties).map((col, idx) => {
              // col can be a string or an object
              const colName = typeof col === 'string' ? col : col.column_name || col.name || col;
              const isPK = typeof col === 'object' && col.is_primary_key;
              const isFK = typeof col === 'object' && col.is_foreign_key;

              let className = styles.propertyItem;
              if (isPK && isFK) {
                className += ` ${styles.bothKey}`;
              } else if (isPK) {
                className += ` ${styles.primaryKey}`;
              } else if (isFK) {
                className += ` ${styles.foreignKey}`;
              }

              return (
                <li key={colName + idx} className={className}>
                  {isPK && <span title="Primary Key" className={styles.primaryKeyIcon}>🔑</span>}
                  {isFK && <span title="Foreign Key" className={styles.foreignKeyIcon}>🔗</span>}
                  <span className={styles.propertyName}>{colName}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ),
    },
    position: table.position,
    type: table.id === '1' ? 'input' : undefined,
  }));

  const edges = generateEdges(tables);

  return (
    <ReactFlowProvider>
      <div className={`${styles.container} ${styles.hideWatermark}`}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          style={{ background: '#fafbfc', borderRadius: 8, border: '1px solid #eee' }}
        />
        <ZoomControls />
      </div>
    </ReactFlowProvider>
  );
};

export default EntityRelationshipDiagram; 